import React, { useState, useRef } from "react";
import styles from "./DropdownMultiselect.module.scss";
import CustomCheckbox from "../CustomCheckbox/CustomCheckbox";
import useClickOutside from "../../CustomHooks/useClickOutside";
import DropdownContainer from "../DropdownContainer/DropdownContainer";

export type DropdownItem = {
  code: number | string;
  value: string;
};

export type DropdownItemsGroup =
  | {
      [category: string]: DropdownItem[];
    }
  | DropdownItem[];

interface DropdownMultiselectProps {
  dropdownName: string;
  items: DropdownItemsGroup;
  selectedItems: DropdownItem[];
  handleSelectItemCallback: (selected: DropdownItem[]) => void;
  addScroll?: boolean;
  size?: "sm" | "md";
  className?: string;
  errorMsg?: string;
  invalid?: boolean;
}

const DropdownMultiselect: React.FC<DropdownMultiselectProps> = ({
  dropdownName,
  items,
  selectedItems,
  handleSelectItemCallback,
  addScroll = false,
  size = "md",
  className,
  errorMsg,
  invalid,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  useClickOutside(dropdownRef, () => setIsOpen(false));

  const toggleDropdown = () => setIsOpen((prev) => !prev);

  const handleItemToggle = (
    item: DropdownItem,
    isCurrentlySelected: boolean,
    event: React.MouseEvent
  ) => {
    event.stopPropagation();

    const shouldBeChecked = !isCurrentlySelected;
    let newSelection: DropdownItem[];

    if (shouldBeChecked) {
      newSelection = [...selectedItems, item];
    } else {
      newSelection = selectedItems.filter((s) => s.code !== item.code);
    }
    handleSelectItemCallback(newSelection);
  };

  const renderItemRow = (item: DropdownItem) => {
    const isSelected = selectedItems.some((s) => s.code === item.code);

    return (
      <div
        key={item.code}
        className={`${styles.checkbox_wrapper} ${isSelected ? styles.selected : ""}`}
        onClick={(e) => handleItemToggle(item, isSelected, e)}
      >
        <CustomCheckbox
          label={item.value}
          checked={isSelected}
          screenName="growthplan"
        />
      </div>
    );
  };

  function renderGroupedItems() {
    if (Array.isArray(items)) {
      return items.map(renderItemRow);
    }

    return Object.entries(items).map(([category, metricArray]) => (
      <div key={category}>
        <div className={styles.section_title}>{category}</div>
        {metricArray.map(renderItemRow)}
      </div>
    ));
  }

  const selectedLabels =
    selectedItems && selectedItems.length > 0
      ? selectedItems?.map((i) => i.value).join(", ")
      : "";

  return (
    <DropdownContainer
      label={dropdownName}
      value={selectedLabels}
      isOpen={isOpen}
      onToggle={toggleDropdown}
      dropdownRef={dropdownRef}
      size={size}
      className={className}
      errorMsg={errorMsg}
      invalid={invalid}
      addScroll={addScroll}
    >
      {renderGroupedItems()}
    </DropdownContainer>
  );
};

export default DropdownMultiselect;
